import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  QrCode, CheckCircle, XCircle, Loader2, Camera, Users,
  TrendingUp, Calendar as CalendarIcon, Clock, User, Package,
  ScanLine, ChevronRight, RotateCcw, AlertCircle, CameraOff,
} from "lucide-react";
import axios from "axios";
import jsQR from "jsqr";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Calendar } from "../../components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── small helpers ────────────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return "—";
  try { return format(parseISO(iso), "h:mm a"); } catch { return iso; }
}

function StatusBadge({ status }) {
  const styles = {
    active:  "bg-green-500/20 text-green-400 border-green-500/30",
    used:    "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    expired: "bg-red-500/20 text-red-400 border-red-500/30",
    invalid: "bg-neutral-500/20 text-neutral-400 border-neutral-500/30",
  };
  return (
    <Badge className={`border text-xs ${styles[status] ?? styles.invalid}`}>
      {(status ?? "invalid").toUpperCase()}
    </Badge>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export const AdminAttendance = () => {

  // ── context selection ────────────────────────────────────────────────────
  const [step, setStep]                 = useState("select"); // "select" | "scanner"
  const [contextType, setContextType]   = useState("classes"); // "classes" | "events"
  const [todayContext, setTodayContext]  = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);  // selected schedule or event

  // ── scanner state ────────────────────────────────────────────────────────
  const [qrHash, setQrHash]       = useState("");
  const [scanning, setScanning]   = useState(false);
  const [scanHistory, setScanHistory] = useState([]);  // newest first, max 20
  const [cameraActive, setCameraActive] = useState(false);

  // ── history tab ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]     = useState("scanner");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [attendanceDates, setAttendanceDates] = useState([]);
  const [metrics, setMetrics]         = useState(null);

  // ── camera refs ──────────────────────────────────────────────────────────
  // videoRef and canvasRef point to elements that live inside the scanner
  // card (step === "scanner"). They are mounted before the user taps
  // "Start Camera", so videoRef.current is valid when startCamera() runs.
  const videoRef            = useRef(null);
  const canvasRef           = useRef(null);
  const streamRef           = useRef(null);
  const animationFrameRef   = useRef(null);
  const scanInFlightRef     = useRef(false);
  const lastHashRef         = useRef(null);
  const lastScanTimeRef     = useRef(0);

  // Keep a stable ref to the latest submitScan so the rAF loop always calls
  // the version that closes over the current selectedItem/contextType.
  const submitScanRef = useRef(null);

  // ── initial data load ────────────────────────────────────────────────────

  useEffect(() => {
    fetchTodayContext();
    fetchAttendanceDates();
    fetchMetrics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedDate && activeTab === "history") {
      fetchAttendanceByDate(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach stream to video element AFTER cameraActive=true triggers a render.
  // Because the <video> lives inside step==="scanner" (already rendered before
  // the user taps "Start Camera"), videoRef.current is guaranteed non-null here.
  useEffect(() => {
    if (cameraActive && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play()
        .then(() => startScanLoop())
        .catch((err) => {
          console.error("Video play failed:", err);
          toast.error("Camera play failed. Please try again.");
          stopCamera();
        });
    }
  }, [cameraActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // ── data fetching ─────────────────────────────────────────────────────────

  const fetchTodayContext = async () => {
    setContextLoading(true);
    try {
      const res = await axios.get(`${API}/admin/attendance/today-context`);
      setTodayContext(res.data);
      return res.data;
    } catch {
      toast.error("Could not load today's schedule");
      return null;
    } finally {
      setContextLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const res = await axios.get(`${API}/admin/attendance/metrics`);
      setMetrics(res.data);
    } catch { /* non-critical */ }
  };

  const fetchAttendanceDates = async () => {
    try {
      const res = await axios.get(`${API}/admin/attendance/dates`);
      setAttendanceDates(res.data || []);
    } catch { /* non-critical */ }
  };

  const fetchAttendanceByDate = async (date) => {
    setCalendarLoading(true);
    try {
      const res = await axios.get(`${API}/admin/attendance/calendar?date=${date}`);
      setAttendanceData(res.data);
    } catch {
      toast.error("Failed to load attendance data");
    } finally {
      setCalendarLoading(false);
    }
  };

  // ── scan submission ───────────────────────────────────────────────────────

  // Always call submitScanRef.current() from the rAF loop — this is updated
  // every render so the loop never holds a stale closure.
  const submitScan = useCallback(async (hash) => {
    if (scanInFlightRef.current || !selectedItem) return;
    scanInFlightRef.current = true;
    setScanning(true);

    const params = new URLSearchParams({ qr_hash: hash });
    if (contextType === "classes") params.set("class_schedule_id", selectedItem.id);
    if (contextType === "events")  params.set("event_id", selectedItem.id);

    try {
      const res = await axios.post(`${API}/admin/attendance/scan-enhanced?${params}`);
      const entry = { ...res.data, scannedAt: new Date() };
      setScanHistory((prev) => [entry, ...prev].slice(0, 20));

      if (res.data.status) {
        fetchMetrics();
        // Refresh context counts and update the selected item in-place
        const fresh = await fetchTodayContext();
        if (fresh) {
          const list = contextType === "classes" ? fresh.classes : fresh.events;
          const updated = list?.find((x) => x.id === selectedItem.id);
          if (updated) setSelectedItem(updated);
        }
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Scan failed. Try again.";
      setScanHistory((prev) => [
        { status: false, message, qr_status: "invalid", scannedAt: new Date() },
        ...prev,
      ].slice(0, 20));
    } finally {
      scanInFlightRef.current = false;
      setScanning(false);
    }
  }, [contextType, selectedItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ref in sync every render
  submitScanRef.current = submitScan;

  const handleManualScan = (e) => {
    e?.preventDefault();
    const hash = qrHash.trim();
    if (!hash) { toast.error("Enter a QR code hash"); return; }
    submitScanRef.current(hash);
    setQrHash("");
  };

  // ── camera ────────────────────────────────────────────────────────────────

  const startScanLoop = () => {
    const loop = () => {
      const video  = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });
        if (code?.data) {
          const now = Date.now();
          const isDuplicate =
            lastHashRef.current === code.data &&
            now - lastScanTimeRef.current < 3000;
          if (!isDuplicate) {
            lastHashRef.current   = code.data;
            lastScanTimeRef.current = now;
            submitScanRef.current(code.data); // always latest closure
          }
        }
      }
      animationFrameRef.current = requestAnimationFrame(loop);
    };
    animationFrameRef.current = requestAnimationFrame(loop);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      // Setting cameraActive triggers useEffect, which runs AFTER React
      // re-renders — at that point videoRef.current is valid and we attach
      // the stream + call play() there, not here.
      setCameraActive(true);
    } catch (err) {
      toast.error("Could not access camera. Check browser permissions.");
      console.error("getUserMedia error:", err);
    }
  };

  const stopCamera = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  const handleChangeContext = () => {
    stopCamera();
    setStep("select");
    setSelectedItem(null);
    setScanHistory([]);
    fetchTodayContext();
  };

  // ── derived ───────────────────────────────────────────────────────────────

  const items = contextType === "classes"
    ? (todayContext?.classes ?? [])
    : (todayContext?.events  ?? []);

  const datesWithAttendance = attendanceDates.map((d) => new Date(d.date));

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div data-testid="admin-attendance">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Attendance</h1>
        <p className="text-neutral-400">QR check-in for today's classes and events</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-neutral-900 border border-neutral-800">
          <TabsTrigger value="scanner" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            <ScanLine className="w-4 h-4 mr-2" />Scanner
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            <CalendarIcon className="w-4 h-4 mr-2" />History
          </TabsTrigger>
        </TabsList>

        {/* ══════════════════ SCANNER TAB ══════════════════ */}
        <TabsContent value="scanner">
          <AnimatePresence mode="wait">

            {/* ── STEP 1: SELECT CONTEXT ─────────────────── */}
            {step === "select" && (
              <motion.div
                key="select"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
              >
                <Card className="card-empire max-w-xl mx-auto lg:max-w-none">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-empire-gold" />
                      What are you scanning for today?
                    </CardTitle>
                    <p className="text-sm text-neutral-400 mt-1">
                      Only today's scheduled items appear here. You cannot scan for past or future items.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Type toggle */}
                    <div className="flex gap-2">
                      {["classes", "events"].map((type) => (
                        <button
                          key={type}
                          onClick={() => { setContextType(type); setSelectedItem(null); }}
                          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold capitalize transition-colors
                            ${contextType === type
                              ? "bg-empire-gold text-black"
                              : "bg-neutral-800 text-neutral-400 hover:text-white"}`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>

                    {/* Item list */}
                    {contextLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
                      </div>
                    ) : items.length === 0 ? (
                      <div className="text-center py-12">
                        <AlertCircle className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
                        <p className="text-neutral-400 font-medium">No {contextType} scheduled for today</p>
                        <p className="text-neutral-600 text-sm mt-1">Check back on the day of the event or class.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {items.map((item) => {
                          const isSelected  = selectedItem?.id === item.id;
                          const label       = contextType === "classes" ? (item.class_name || item.name) : item.name;
                          const checkedIn   = item.checked_in_count ?? 0;
                          const total       = item.total_bookings ?? 0;

                          return (
                            <button
                              key={item.id}
                              onClick={() => setSelectedItem(isSelected ? null : item)}
                              className={`w-full text-left p-4 rounded-xl border transition-all
                                ${isSelected
                                  ? "border-empire-gold bg-empire-gold/10"
                                  : "border-neutral-800 bg-neutral-900 hover:border-neutral-700"}`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className={`font-semibold truncate ${isSelected ? "text-empire-gold" : "text-white"}`}>
                                    {label}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1 text-sm text-neutral-400 flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {fmtTime(item.start_datetime)}
                                      {item.end_datetime && ` – ${fmtTime(item.end_datetime)}`}
                                    </span>
                                    {item.instructor_name && (
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" /> {item.instructor_name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <p className="text-green-400 font-bold text-lg leading-none">{checkedIn}</p>
                                    <p className="text-neutral-500 text-xs">/ {total} in</p>
                                  </div>
                                  {isSelected && <CheckCircle className="w-5 h-5 text-empire-gold flex-shrink-0" />}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* CTA */}
                    {selectedItem && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                        <Button
                          onClick={() => setStep("scanner")}
                          className="w-full btn-empire h-12 text-base mt-2"
                        >
                          <ScanLine className="w-5 h-5 mr-2" />
                          Start Scanning
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* ── STEP 2: SCANNER ────────────────────────── */}
            {step === "scanner" && selectedItem && (
              <motion.div
                key="scanner"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="space-y-4"
              >
                {/* Context header bar */}
                <Card className="card-empire border-empire-gold/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-0.5">Scanning for</p>
                        <p className="text-white font-bold text-lg leading-tight truncate">
                          {contextType === "classes"
                            ? (selectedItem.class_name || selectedItem.name)
                            : selectedItem.name}
                        </p>
                        <div className="flex items-center gap-3 mt-0.5 text-sm text-neutral-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {fmtTime(selectedItem.start_datetime)}
                            {selectedItem.end_datetime && ` – ${fmtTime(selectedItem.end_datetime)}`}
                          </span>
                          {selectedItem.instructor_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" /> {selectedItem.instructor_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-400 leading-none">
                            {selectedItem.checked_in_count ?? 0}
                          </p>
                          <p className="text-xs text-neutral-500">/ {selectedItem.total_bookings ?? 0} in</p>
                        </div>
                        <Button
                          onClick={handleChangeContext}
                          variant="outline"
                          size="sm"
                          className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Change
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                  {/* ── Left: camera + manual ── */}
                  <div className="space-y-4">
                    <Card className="card-empire">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white flex items-center gap-2 text-base">
                          <Camera className="w-5 h-5 text-empire-gold" />
                          Camera Scanner
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {/*
                          The <video> is ALWAYS rendered here (inside step==="scanner").
                          It is mounted in the DOM BEFORE the user taps "Start Camera",
                          so videoRef.current is valid when startCamera() runs.
                          We hide it via CSS when the camera is not active.
                        */}
                        <div className={`relative bg-black rounded-xl overflow-hidden aspect-video ${!cameraActive && "hidden"}`}>
                          <video
                            ref={videoRef}
                            className="w-full h-full object-cover"
                            playsInline
                            muted
                          />
                          {/* Targeting reticle */}
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="relative w-52 h-52">
                              <div className="absolute inset-0 border-2 border-empire-gold/60 rounded-xl" />
                              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-empire-gold rounded-tl-xl" />
                              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-empire-gold rounded-tr-xl" />
                              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-empire-gold rounded-bl-xl" />
                              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-empire-gold rounded-br-xl" />
                            </div>
                          </div>
                          {scanning && (
                            <div className="absolute bottom-3 inset-x-0 flex justify-center">
                              <div className="bg-black/80 text-empire-gold text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Checking…
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Canvas — always hidden, used for frame capture */}
                        <canvas ref={canvasRef} className="hidden" />

                        {!cameraActive ? (
                          <Button onClick={startCamera} className="w-full btn-empire h-12">
                            <Camera className="w-5 h-5 mr-2" />
                            Start Camera
                          </Button>
                        ) : (
                          <>
                            <p className="text-xs text-neutral-500 text-center">
                              Point the camera at a QR code — it scans automatically
                            </p>
                            <Button
                              onClick={stopCamera}
                              variant="outline"
                              className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 h-10"
                            >
                              <CameraOff className="w-4 h-4 mr-2" />
                              Stop Camera
                            </Button>
                          </>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="card-empire">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white flex items-center gap-2 text-base">
                          <QrCode className="w-5 h-5 text-empire-gold" />
                          Manual Entry
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={handleManualScan} className="flex gap-2">
                          <Input
                            value={qrHash}
                            onChange={(e) => setQrHash(e.target.value)}
                            placeholder="Paste QR hash…"
                            className="input-empire flex-1"
                            data-testid="qr-input"
                          />
                          <Button
                            type="submit"
                            disabled={scanning || !qrHash.trim()}
                            className="btn-empire px-5"
                            data-testid="scan-btn"
                          >
                            {scanning
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : "Check In"}
                          </Button>
                        </form>
                      </CardContent>
                    </Card>
                  </div>

                  {/* ── Right: scan log ── */}
                  <Card className="card-empire">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white flex items-center justify-between text-base">
                        <span className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-empire-gold" />
                          Scan Log
                        </span>
                        {scanHistory.length > 0 && (
                          <span className="text-xs text-neutral-500">{scanHistory.length} scans</span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {scanHistory.length === 0 ? (
                        <div className="text-center py-12">
                          <ScanLine className="w-10 h-10 text-neutral-700 mx-auto mb-3" />
                          <p className="text-neutral-500 text-sm">No scans yet</p>
                          <p className="text-neutral-600 text-xs mt-1">Results appear here as you scan</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                          <AnimatePresence initial={false}>
                            {scanHistory.map((result, idx) => (
                              <ScanResultCard key={idx} result={result} />
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </TabsContent>

        {/* ══════════════════ HISTORY TAB ══════════════════ */}
        <TabsContent value="history">
          {/* Summary stats */}
          {metrics && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {[
                { label: "Classes Today", value: metrics.total_classes,        color: "text-blue-400",   bg: "bg-blue-500/20",   Icon: CalendarIcon },
                { label: "Expected",      value: metrics.total_expected,        color: "text-empire-gold", bg: "bg-empire-gold/20", Icon: Users },
                { label: "Checked In",   value: metrics.total_checked_in,      color: "text-green-400",  bg: "bg-green-500/20",  Icon: CheckCircle },
                { label: "Attendance",   value: `${metrics.overall_attendance_rate}%`, color: "text-purple-400", bg: "bg-purple-500/20", Icon: TrendingUp },
              ].map(({ label, value, color, bg, Icon }) => (
                <Card key={label} className="card-empire">
                  <CardContent className="p-4">
                    <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center mb-2`}>
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                    <p className="text-sm text-neutral-400">{label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar picker */}
            <Card className="card-empire">
              <CardHeader>
                <CardTitle className="text-white">Select Date</CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border border-neutral-800"
                  classNames={{
                    day_selected: "bg-empire-gold text-black hover:bg-empire-gold/90",
                    day_today: "bg-neutral-800 text-white",
                  }}
                  modifiers={{ hasAttendance: datesWithAttendance }}
                  modifiersClassNames={{ hasAttendance: "border-2 border-green-500/50" }}
                />
                <p className="text-xs text-neutral-500 mt-2 text-center">
                  Green border = attendance recorded
                </p>
              </CardContent>
            </Card>

            {/* Attendance table */}
            <div className="lg:col-span-2">
              <Card className="card-empire h-full">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span>{format(selectedDate, "MMMM d, yyyy")}</span>
                    {attendanceData && (
                      <Badge className="bg-empire-gold/20 text-empire-gold">
                        {attendanceData.total_attendees} attendees
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {calendarLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
                    </div>
                  ) : attendanceData?.attendance_records?.length > 0 ? (
                    <>
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {[
                          { label: "Attendees", value: attendanceData.total_attendees, cls: "text-white" },
                          { label: "Classes",   value: attendanceData.unique_classes,  cls: "text-white" },
                          { label: "Revenue",   value: `KES ${attendanceData.total_revenue?.toLocaleString()}`, cls: "text-empire-gold" },
                        ].map(({ label, value, cls }) => (
                          <div key={label} className="bg-neutral-800/50 rounded-lg p-3 text-center">
                            <div className={`text-2xl font-bold ${cls}`}>{value}</div>
                            <div className="text-xs text-neutral-400">{label}</div>
                          </div>
                        ))}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-neutral-800">
                              {["Guest", "Class", "Time", "Instructor", "Price", "Package"].map((h) => (
                                <th key={h} className="text-left py-3 px-2 text-neutral-400 font-medium text-sm">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {attendanceData.attendance_records.map((record) => (
                              <tr key={record.id} className="border-b border-neutral-800/50 hover:bg-neutral-800/30">
                                <td className="py-3 px-2">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                                    <span className="text-white font-medium text-sm">{record.guest_name}</span>
                                  </div>
                                </td>
                                <td className="py-3 px-2 text-neutral-300 text-sm">{record.class_name || "—"}</td>
                                <td className="py-3 px-2 text-neutral-400 text-sm">{record.class_time || "—"}</td>
                                <td className="py-3 px-2 text-neutral-300 text-sm">{record.instructor_name || "—"}</td>
                                <td className="py-3 px-2 text-empire-gold font-medium text-sm">
                                  KES {record.class_price?.toLocaleString()}
                                </td>
                                <td className="py-3 px-2">
                                  {record.package_name ? (
                                    <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                                      <Package className="w-3 h-3 mr-1" />{record.package_name}
                                    </Badge>
                                  ) : (
                                    <span className="text-neutral-500 text-sm">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <CalendarIcon className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
                      <p className="text-neutral-500">No attendance records for this date</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Charts */}
          {metrics?.class_metrics?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {metrics.total_expected > 0 && (
                <Card className="card-empire">
                  <CardHeader><CardTitle className="text-white">Today's Overview</CardTitle></CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: "Checked In", value: metrics.total_checked_in },
                              { name: "Pending", value: Math.max(0, metrics.total_expected - metrics.total_checked_in) },
                            ]}
                            cx="50%" cy="50%" innerRadius={55} outerRadius={75}
                            paddingAngle={5} dataKey="value"
                          >
                            <Cell fill="#22c55e" />
                            <Cell fill="#404040" />
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333", borderRadius: "8px" }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-6 mt-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-sm text-neutral-400">Checked In ({metrics.total_checked_in})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-neutral-600" />
                        <span className="text-sm text-neutral-400">Pending ({metrics.total_expected - metrics.total_checked_in})</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card className="card-empire">
                <CardHeader><CardTitle className="text-white">Attendance by Class</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={metrics.class_metrics} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis type="number" stroke="#666" tick={{ fill: "#999", fontSize: 10 }} />
                        <YAxis dataKey="class_name" type="category" stroke="#666" tick={{ fill: "#999", fontSize: 10 }} width={100} />
                        <Tooltip contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333", borderRadius: "8px" }} />
                        <Bar dataKey="checked_in" fill="#22c55e" name="Checked In" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="expected" fill="#333" name="Expected" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
};

// ─── scan result card ─────────────────────────────────────────────────────────

function ScanResultCard({ result }) {
  const isSuccess  = result.status;
  const guest      = result.booking?.guest_name ?? result.package?.guest_name ?? null;
  const bookingNum = result.booking?.booking_number ?? null;
  const type       = result.booking_type;
  const credits    = result.credits_remaining;
  const pkgName    = result.package_name;
  const time       = result.scannedAt ? format(new Date(result.scannedAt), "h:mm:ss a") : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className={`rounded-xl border p-3.5 ${
        isSuccess
          ? "border-green-500/30 bg-green-500/5"
          : "border-red-500/20 bg-red-500/5"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
          ${isSuccess ? "bg-green-500/20" : "bg-red-500/20"}`}
        >
          {isSuccess
            ? <CheckCircle className="w-4 h-4 text-green-500" />
            : <XCircle className="w-4 h-4 text-red-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className={`font-semibold text-sm ${isSuccess ? "text-green-400" : "text-red-400"}`}>
              {result.message}
            </p>
            <StatusBadge status={result.qr_status} />
          </div>
          {guest && <p className="text-white text-sm font-medium mt-0.5">{guest}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {bookingNum && (
              <span className="text-neutral-500 text-xs font-mono">{bookingNum}</span>
            )}
            {type && (
              <Badge variant="outline" className="border-neutral-700 text-neutral-400 text-xs py-0">
                {type === "package" && <Package className="w-2.5 h-2.5 mr-1" />}
                {type}
              </Badge>
            )}
            {type === "package" && credits !== undefined && (
              <span className="text-xs text-purple-400">
                {credits} credit{credits !== 1 ? "s" : ""} left
              </span>
            )}
            {pkgName && <span className="text-xs text-neutral-500">{pkgName}</span>}
            {time && <span className="text-xs text-neutral-600 ml-auto">{time}</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
